import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Edit, Trash2, Plus, ArrowLeft, LogOut } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminShows = () => {
  const navigate = useNavigate();
  const { user, loading, logout } = useAuth();
  const [shows, setShows] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingShow, setEditingShow] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    date: '',
    time: '',
    location: '',
    ticket_link: '',
    show_info: ''
  });

  useEffect(() => {
    if (!loading && !user) {
      navigate('/admin/login');
    } else if (user) {
      fetchShows();
    }
  }, [user, loading, navigate]);

  const fetchShows = async () => {
    try {
      const response = await axios.get(`${API}/shows`);
      setShows(response.data);
    } catch (error) {
      console.error('Error fetching shows:', error);
      toast.error('Failed to load shows');
    }
  };

  const handleLogout = async () => {
    await logout();
    toast.success('Logged out successfully');
    navigate('/');
  };

  const openDialog = (show = null) => {
    if (show) {
      setEditingShow(show);
      setFormData({
        title: show.title,
        date: show.date,
        time: show.time,
        location: show.location,
        ticket_link: show.ticket_link || '',
        show_info: show.show_info || ''
      });
    } else {
      setEditingShow(null);
      setFormData({
        title: '',
        date: '',
        time: '',
        location: '',
        ticket_link: '',
        show_info: ''
      });
    }
    setIsDialogOpen(true);
  };

  const closeDialog = () => {
    setIsDialogOpen(false);
    setEditingShow(null);
    setFormData({
      title: '',
      date: '',
      time: '',
      location: '',
      ticket_link: '',
      show_info: ''
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingShow) {
        await axios.put(
          `${API}/shows/${editingShow.id}`,
          formData,
          { withCredentials: true }
        );
        toast.success('Show updated successfully');
      } else {
        await axios.post(
          `${API}/shows`,
          formData,
          { withCredentials: true }
        );
        toast.success('Show created successfully');
      }
      closeDialog();
      fetchShows();
    } catch (error) {
      console.error('Error saving show:', error);
      toast.error('Failed to save show');
    }
  };

  const handleDelete = async (showId) => {
    if (!window.confirm('Are you sure you want to delete this show?')) {
      return;
    }
    try {
      await axios.delete(`${API}/shows/${showId}`, { withCredentials: true });
      toast.success('Show deleted successfully');
      fetchShows();
    } catch (error) {
      console.error('Error deleting show:', error);
      toast.error('Failed to delete show');
    }
  };

  if (loading) {
    return (
      <div className="admin-loading">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="admin-container">
      {/* Header */}
      <header className="admin-header">
        <div className="admin-header-content">
          <div className="admin-logo-section" onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
            <img 
              src="https://customer-assets.emergentagent.com/job_lethbridge-angels/artifacts/znfka10j_RA-blacktransparent%20cropped%20no%20vol.png" 
              alt="Rebel Angels" 
              className="admin-brand-logo"
            />
          </div>
          <div className="admin-user-section">
            <img src={user.picture} alt={user.name} className="admin-user-avatar" />
            <span className="admin-user-name">{user.name}</span>
            <Button variant="outline" className="admin-logout-button" onClick={handleLogout}>
              <LogOut className="button-icon" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="admin-main">
        <div className="admin-content">
          <div className="admin-page-header">
            <Button className="admin-add-button" onClick={() => navigate('/admin/dashboard')}>
              <ArrowLeft className="button-icon" />
              Back to Dashboard
            </Button>
          </div>

          <div className="admin-title-row">
            <h1 className="admin-page-title">Manage Shows</h1>
            <Button className="admin-add-button" onClick={() => openDialog()}>
              <Plus className="button-icon" />
              Add Show
            </Button>
          </div>

          <div className="admin-shows-grid">
            {shows.length === 0 ? (
              <p className="admin-empty-message">No shows yet. Add your first show!</p>
            ) : (
              shows.map((show) => (
                <Card key={show.id} className="admin-show-card">
                  <CardContent className="admin-show-card-content">
                    <h3 className="admin-show-title">{show.title}</h3>
                    <div className="admin-show-details">
                      <p><strong>Date:</strong> {show.date}</p>
                      <p><strong>Time:</strong> {show.time}</p>
                      <p><strong>Location:</strong> {show.location}</p>
                      {show.ticket_link && (
                        <p><strong>Ticket Link:</strong> {show.ticket_link}</p>
                      )}
                      {show.show_info && (
                        <p><strong>Show Info:</strong> {show.show_info}</p>
                      )}
                    </div>
                    <div className="admin-show-actions">
                      <Button variant="outline" size="sm" onClick={() => openDialog(show)}>
                        <Edit className="button-icon-sm" />
                        Edit
                      </Button>
                      <Button variant="destructive" size="sm" onClick={() => handleDelete(show.id)}>
                        <Trash2 className="button-icon-sm" />
                        Delete
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </div>
      </main>

      {/* Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="admin-dialog">
          <DialogHeader>
            <DialogTitle>{editingShow ? 'Edit Show' : 'Add New Show'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="admin-form">
            <div className="admin-form-group">
              <label className="admin-form-label">Title *</label>
              <Input
                required
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Summer Rock Festival"
              />
            </div>
            <div className="admin-form-group">
              <label className="admin-form-label">Date *</label>
              <Input
                required
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                placeholder="July 15, 2025"
              />
            </div>
            <div className="admin-form-group">
              <label className="admin-form-label">Time *</label>
              <Input
                required
                value={formData.time}
                onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                placeholder="8:00 PM - 11:00 PM"
              />
            </div>
            <div className="admin-form-group">
              <label className="admin-form-label">Location *</label>
              <Input
                required
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                placeholder="Galt Gardens, Lethbridge"
              />
            </div>
            <div className="admin-form-group">
              <label className="admin-form-label">Ticket Link</label>
              <Input
                value={formData.ticket_link}
                onChange={(e) => setFormData({ ...formData, ticket_link: e.target.value })}
                placeholder="https://tickets.example.com"
              />
            </div>
            <div className="admin-form-group">
              <label className="admin-form-label">Show Info</label>
              <Input
                value={formData.show_info}
                onChange={(e) => setFormData({ ...formData, show_info: e.target.value })}
                placeholder="Additional details (e.g., Special guest, Age restrictions, Free admission)"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={closeDialog}>
                Cancel
              </Button>
              <Button type="submit">
                {editingShow ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminShows;
